import { AfterViewInit, Component, OnInit, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { DashboardService } from "../../services/dashboard.service";
import { ProjectService } from "../../services/project.service";

@Component({
  selector: 'app-project-list',
  templateUrl: 'project-list.component.html',
  styleUrls: ['project-list.component.css']
})
export class ProjectListComponent implements OnInit, AfterViewInit {

  Math = Math;
  pageSize = 10;

  // Pagination
  currentPage = 1;
  totalProjectCount = 0;
  totalPages = 0;
  pageNumbers: number[] = [];
  nextPageUrl: string | null = null;
  prevPageUrl: string | null = null;

  // Projects data
  projectsList$: any[] = [];
  filteredProjectsList: any[] = [];

  // Filter dropdowns data
  agencyArray: any[] = [];
  districtList: any[] = [];
  monitoringDatesList: any[] = [];

  // ✅ STATIC options for Priority and Stage — no backend needed
  priorityOptions: { value: string; label: string }[] = [
    { value: 'Priority 1', label: 'Priority 1' },
    { value: 'Priority 2', label: 'Priority 2' },
  ];

  // ✅ Monitoring options built dynamically from backend (split by comma)
  monitoringOptions: { value: string; label: string }[] = [];

  stageOptions: { value: string; label: string }[] = [
    { value: 'DPR',     label: 'DPR'     },
    { value: 'Ongoing', label: 'Ongoing' },
  ];

  // Selected filters from top dropdowns
  selectedAgencyId: string = '';
  selectedDistrict: string = '';

  // Sidebar multi-select filters — ARRAYS
  selectedMonitoring: string[] = [];
  selectedAgencies:   string[] = [];
  selectedPriorities: string[] = [];
  selectedDistricts:  string[] = [];
  selectedStages:     string[] = [];

  // UI state
  isSidebarOpen = false;
  searchText    = '';
  isLoading     = false;

  // Search debouncing
  private searchSubject = new Subject<string>();

  // Sort option
  selectedSort: string = 'latest';

  displayedColumns: string[] = [
    "project_name", "districts", "monitoring", "priority",
    "stage", "progress", "jms", "A_3A", "C_3C", "D_3D",
    "G_3G", "H_3H_money", "H_3H_area", "posession",
    "mutation", "govt_posession", "govt_mutation", "progress_percentage"
  ];

  public dataSource = new MatTableDataSource<any>();
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(
    private dashboardService: DashboardService,
    private route: ActivatedRoute,
    private router: Router,
    private projectService: ProjectService
  ) { }

  ngOnInit(): void {
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    ).subscribe(searchValue => {
      this.searchText = searchValue;
      this.currentPage = 1;
      this.loadProjects();
    });

    this.loadFilterDropdowns();
    this.loadProjects();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  // =========== LOAD FILTER DROPDOWNS ===========
  loadFilterDropdowns(): void {
    this.projectService.getAgencies().subscribe({
      next: (res) => { this.agencyArray = res; },
      error: (err) => console.error('Error loading agencies:', err)
    });

    this.projectService.getDistricts().subscribe({
      next: (res) => { this.districtList = res; },
      error: (err) => console.error('Error loading districts:', err)
    });

    // ✅ Monitoring: API returns e.g. [{"value":"PMG,PRAGATI,","label":"PMG,PRAGATI,"}]
    // Split each item by comma → individual sidebar options
    // "PM laid foundation,CM/MIN (RTH),PMG,PRAGATI," → 4 separate items
    this.projectService.getMonitoringDates().subscribe({
      next: (res) => {
        console.log('Monitoring raw response:', res);

        const seen  = new Set<string>();
        const items: { value: string; label: string }[] = [];

        (res as any[]).forEach(item => {
          const raw: string = item.value ?? item.label ?? item.date ?? '';

          raw
            .split(',')
            .map((t: string) => t.trim())
            .filter((t: string) => t.length > 0)
            .forEach((token: string) => {
              if (!seen.has(token)) {
                seen.add(token);
                items.push({ value: token, label: token });
              }
            });
        });

        this.monitoringOptions  = items;
        this.monitoringDatesList = items; // keeps *ngIf="monitoringDatesList.length > 0" working
        console.log('Monitoring options after split:', this.monitoringOptions);
      },
      error: (err) => console.error('Error loading monitoring list:', err)
    });
  }

  // =========== LOAD PROJECTS WITH FILTERS ===========
  loadProjects(): void {
    this.isLoading = true;

    const params: any = {
      sort: this.selectedSort,
      // ✅ PAGINATION FIX: always include page so backend returns the correct page.
      // Previously this was commented out in project.service.ts → always showed page 1.
      page: this.currentPage,
    };

    if (this.searchText?.trim()) {
      params.search = this.searchText.trim();
    }

    if (this.selectedAgencyId) {
      params.agency = this.selectedAgencyId;
    }

    if (this.selectedDistrict && this.selectedDistrict !== 'Default') {
      params.district = this.selectedDistrict;
    }

    // Sidebar district overrides top-bar district
    if (this.selectedDistricts.length > 0) {
      params.district = this.selectedDistricts.join(',');
    }

    // ✅ Priority — static values ("Priority 1", "Priority 2") sent in payload
    if (this.selectedPriorities.length > 0) {
      params.priority = this.selectedPriorities.join(',');
    }

    // ✅ Stage — static values ("DPR", "Ongoing") sent in payload
    if (this.selectedStages.length > 0) {
      params.stage = this.selectedStages.join(',');
    }

    // ✅ Monitoring — individual tokens re-joined for API: "PMG,PRAGATI"
    if (this.selectedMonitoring.length > 0) {
      params.monitoring = this.selectedMonitoring.join(',');
    }

    // Sidebar agency overrides top-bar agency
    if (this.selectedAgencies.length > 0) {
      params.agency = this.selectedAgencies.join(',');
    }

    console.log('Loading projects with params:', params);

    this.projectService.getMainList(params).subscribe({
      next: (res) => {
        console.log('Projects response:', res);
        this.totalProjectCount    = res.count;
        this.totalPages           = Math.ceil(res.count / this.pageSize);
        this.nextPageUrl          = res.next;
        this.prevPageUrl          = res.previous;
        this.projectsList$        = res.results || [];
        this.filteredProjectsList = [...this.projectsList$];
        this.dataSource.data      = this.projectsList$;
        this.buildPageNumbers();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading projects:', err);
        this.isLoading            = false;
        this.projectsList$        = [];
        this.filteredProjectsList = [];
        this.dataSource.data      = [];
      }
    });
  }

  // =========== PAGINATION ===========
  buildPageNumbers(): void {
    const total   = this.totalPages;
    const current = this.currentPage;
    const pages: number[] = [];

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > 3) pages.push(-1);
      const start = Math.max(2, current - 1);
      const end   = Math.min(total - 1, current + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (current < total - 2) pages.push(-1);
      pages.push(total);
    }

    this.pageNumbers = pages;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.loadProjects();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadProjects();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadProjects();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // =========== SEARCH ===========
  onSearchInput(value: string): void {
    this.searchSubject.next(value);
  }

  // =========== TOP DROPDOWN FILTERS ===========
  onAgencyChange(agencyId: string): void {
    this.selectedAgencyId = agencyId;
    this.selectedAgencies = [];
    this.currentPage      = 1;
    this.loadProjects();
  }

  onDistrictChange(district: string): void {
    this.selectedDistrict  = district;
    this.selectedDistricts = [];
    this.currentPage       = 1;
    this.loadProjects();
  }

  onSortChange(sort: string): void {
    this.selectedSort = sort;
    this.currentPage  = 1;
    this.loadProjects();
  }

  // =========== SIDEBAR MULTI-SELECT FILTERS ===========
  toggleFilter(type: string, value: string): void {
    let arr: string[];

    switch (type) {
      case 'monitoring': arr = this.selectedMonitoring; break;
      case 'agency':
        arr = this.selectedAgencies;
        this.selectedAgencyId = '';
        break;
      case 'priority':  arr = this.selectedPriorities; break;
      case 'district':
        arr = this.selectedDistricts;
        this.selectedDistrict = '';
        break;
      case 'stage': arr = this.selectedStages; break;
      default: return;
    }

    const idx = arr.indexOf(value);
    if (idx > -1) {
      arr.splice(idx, 1);
    } else {
      arr.push(value);
    }

    this.currentPage = 1;
    this.loadProjects();
  }

  isFilterActive(type: string, value: string): boolean {
    switch (type) {
      case 'monitoring': return this.selectedMonitoring.includes(value);
      case 'agency':     return this.selectedAgencies.includes(value);
      case 'priority':   return this.selectedPriorities.includes(value);
      case 'district':   return this.selectedDistricts.includes(value);
      case 'stage':      return this.selectedStages.includes(value);
      default:           return false;
    }
  }

  resetFilters(): void {
    this.selectedMonitoring = [];
    this.selectedAgencies   = [];
    this.selectedPriorities = [];
    this.selectedDistricts  = [];
    this.selectedStages     = [];
    this.selectedAgencyId   = '';
    this.selectedDistrict   = '';
    this.searchText         = '';
    this.selectedSort       = 'latest';
    this.currentPage        = 1;
    this.loadProjects();
  }

  hasActiveFilters(): boolean {
    return (
      this.selectedMonitoring.length > 0 ||
      this.selectedAgencies.length   > 0 ||
      this.selectedPriorities.length > 0 ||
      this.selectedDistricts.length  > 0 ||
      this.selectedStages.length     > 0 ||
      this.selectedAgencyId          !== '' ||
      (this.selectedDistrict !== '' && this.selectedDistrict !== 'Default') ||
      this.searchText                !== ''
    );
  }

  getFilterCount(type: string): number {
    switch (type) {
      case 'monitoring': return this.selectedMonitoring.length;
      case 'agency':     return this.selectedAgencies.length;
      case 'priority':   return this.selectedPriorities.length;
      case 'district':   return this.selectedDistricts.length;
      case 'stage':      return this.selectedStages.length;
      default:           return 0;
    }
  }

  // =========== UI HELPERS ===========
  toggleSidebar(): void {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  gotoProjectDashboard(element: any): void {
    this.router.navigate(["project", element.id]);
  }

  createNewProject(): void {
    this.router.navigate(["create-project"]);
  }

  formatMonitoringLabel(value: string): string {
    if (!value) return 'N/A';
    const isDateLike = /^\d{4}-\d{2}-\d{2}/.test(value.trim());
    if (isDateLike) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
    }
    return value;
  }

  formatMonitoringDate(value: string): string {
    return this.formatMonitoringLabel(value);
  }

  getPriorityClass(priority: string): string {
    if (!priority) return 'tag-default';
    const p = priority.toLowerCase();
    if (p.includes('1') || p.includes('urgent')) return 'tag-urgent';
    if (p.includes('2') || p.includes('high'))   return 'tag-at-risk';
    return 'tag-on-track';
  }

  getStageClass(stage: string): string {
    if (!stage) return 'tag-default';
    const s = stage.toLowerCase();
    if (s.includes('dpr'))     return 'tag-delayed';
    if (s.includes('ongoing')) return 'tag-on-track';
    return 'tag-default';
  }

  getMonitoringClass(monitoring: string): string {
    if (!monitoring) return 'tag-default';
    const m = monitoring.toLowerCase();
    if (m.includes('urgent')) return 'badge-urgent';
    if (m.includes('track'))  return 'badge-on-track';
    if (m.includes('delay'))  return 'badge-delayed';
    if (m.includes('risk'))   return 'badge-at-risk';
    return 'tag-default';
  }

  getFilterLabel(type: string, value: string): string {
    switch (type) {
      case 'priority':
        const p = this.priorityOptions.find(o => o.value === value);
        return p ? p.label : value;
      case 'stage':
        const s = this.stageOptions.find(o => o.value === value);
        return s ? s.label : value;
      case 'monitoring':
        return this.formatMonitoringLabel(value);
      case 'agency':
        const agency = this.agencyArray.find(
          (a: any) => String(a.id) === String(value) || a.id === value 
        );
        return agency ? (agency.name || agency.agency || String(value)) : String(value);
      default:
        return value;
    }
  }
}